import {
  ForwardedRef,
  forwardRef,
  RefObject,
  useMemo,
  useState,
} from 'react'

import Flex from '@react-css/flex'

import iconLaunch from '../../../../../../assets/images/icon_launch.svg'
import iconLaunchFlip from '../../../../../../assets/images/icon_launch_flip.svg'
import iconZoomInBlue from '../../../../../../assets/images/icon_zoom_in_blue.svg'
import iconZoomOutBlue from '../../../../../../assets/images/icon_zoom_out_blue.svg'
import { useBuildingContext } from '../../../../../../contexts/buildingContext'
import { useNavigation } from '../../../../../../hooks/useNavigation'
import { useViewportWidth } from '../../../../../../hooks/useViewportWidth'
import {
  BREAKPOINTS,
  isMobileViewport,
} from '../../../../../../styles/breakpoints'
import { Icon } from '../../../../../common/Icon'
import { IconButton } from '../../../../../common/IconButton'
import { MinimapViewer } from '../../../../../common/MapViewer'
import { Text } from '../../../../../common/Text'
import {
  MinimapPopupHeader,
  MinimapPopupImage,
  MinimapPopupDiv,
} from '../../../styles'
import { ViewerPosition } from '../../../types'

import { resolveCameraPlanCoords } from './cameraPlanCoords'
import { FullScreenTimeNavigation } from './FullscreenTimeNavigation'

interface MinimapPopupProps {
  mapRef: RefObject<MinimapViewer>
  viewerPosition: ViewerPosition
  adjustedOffset?: number
  rightPosition: string | number
  type: string
  minDate?: Date
  maxDate?: Date
  /**
   * Localized capture pose (plan pixels). When set, the FOV marker uses true
   * pose; MinimapViewer omits that point's planned pin to avoid a double-dot.
   */
  trueX?: string | number | null
  trueY?: string | number | null
}

export const MinimapPopup = forwardRef(
  (
    {
      mapRef,
      viewerPosition,
      adjustedOffset = 0,
      rightPosition,
      type,
      minDate,
      maxDate,
      trueX,
      trueY,
    }: MinimapPopupProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { navigateToPointFullscreen } = useNavigation()
    const { state: buildingState } = useBuildingContext()
    const viewportWidth = useViewportWidth()
    const isMobileLayout = viewportWidth < BREAKPOINTS.tablet
    const [collapsed, setCollapsed] = useState(() => isMobileViewport())

    const popupStyle: React.CSSProperties = {
      width: collapsed ? '36px' : undefined,
    }

    if (!isMobileLayout || collapsed) {
      popupStyle.right = rightPosition
    }

    // Same pose source as TourManager: true capture location when available.
    const minimapCameraPoint = useMemo(() => {
      const planned = buildingState.pointData
      const { x, y } = resolveCameraPlanCoords(
        planned.x,
        planned.y,
        trueX,
        trueY,
      )
      return { ...planned, x, y }
    }, [buildingState.pointData, trueX, trueY])

    return (
      <MinimapPopupDiv ref={ref} className={'pnlm-ignore'} style={popupStyle}>
        <div
          style={{
            cursor: 'pointer',
            borderRadius: '2px',
            padding: '5px',
            backgroundColor: '#fff',
            alignItems: 'center',
            justifyContent: 'center',
            display: collapsed ? 'flex' : 'none',
            opacity: '0.5',
          }}
          onClick={() => setCollapsed(false)}
        >
          <Icon icon={iconLaunchFlip} size={16} />
        </div>
        <div style={{ display: collapsed ? 'none' : 'block' }}>
          <MinimapPopupHeader>
            <Flex flexDirection="column">
              <Flex flexDirection="row" justifySpaceBetween>
                <Text
                  size={12}
                  marginBottom={10}
                  color="#7E858E"
                  className="minimap-project-heading"
                >
                  {buildingState.projectData.name},{' '}
                  {buildingState.projectData.location.city},{' '}
                  {buildingState.projectData.location.state_code}
                </Text>
                <Icon
                  icon={iconLaunch}
                  size={16}
                  onClick={() => setCollapsed(true)}
                  style={{ cursor: 'pointer' }}
                />
              </Flex>
              <Flex flexDirection="row" justifyContent={'space-between'}>
                <div>
                  <Text size={16} className="minimap-floor-heading">
                    {buildingState.floorData.name}
                  </Text>
                </div>
                <FullScreenTimeNavigation
                  point={buildingState.pointData}
                  type={type}
                  min={minDate}
                  max={maxDate}
                />
              </Flex>
            </Flex>
          </MinimapPopupHeader>
          <MinimapPopupImage>
            <MinimapViewer
              ref={mapRef}
              selectedDateRange={null}
              image={buildingState.floorData.imageUrl!}
              point={minimapCameraPoint}
              rotation={viewerPosition.yaw + adjustedOffset}
              fov={viewerPosition.hfov}
              tourPoints={buildingState.floorData.points}
              onClickTourPoint={point =>
                navigateToPointFullscreen(
                  point.point_id,
                  window.location.search,
                )
              }
            />
            <IconButton
              icon={iconZoomInBlue}
              size={40}
              primary={false}
              onClick={() => mapRef.current?.zoomIn()}
              style={{ position: 'absolute', right: '8px', bottom: '56px' }}
              classNames="ignore-html2canvas"
            />
            <IconButton
              icon={iconZoomOutBlue}
              size={40}
              primary={false}
              onClick={() => mapRef.current?.zoomOut()}
              style={{ position: 'absolute', right: '8px', bottom: '8px' }}
              classNames="ignore-html2canvas"
            />
          </MinimapPopupImage>
        </div>
      </MinimapPopupDiv>
    )
  },
)

MinimapPopup.displayName = 'MinimapPopup'
